import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Modal, TextButton } from '@safetyhub/ui';
import { adminApi, extractApiError } from '../lib/api';
import { DEFAULT_MEMBER_ROLE_KEY, useSystemRoleOptions } from '../lib/roles';

interface User {
  id: string;
  member: { name: string; phone: string | null } | null;
}

interface RestoreUserDialogProps {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface MembershipEntry {
  companyId: string; // 서버 RestoreMembershipDto.companyId와 일치
  /** 시스템 역할 key(P7-b-2 — 옛 `role` enum 대체) */
  roleKey: string;
}

interface RestorableOrg {
  id: string;
  name: string;
  category: string;
}

interface SkippedOrg {
  id: string;
  name: string;
  category: string;
  reason: string;
}

export default function RestoreUserDialog({
  user,
  isOpen,
  onClose,
  onSuccess,
}: RestoreUserDialogProps) {
  const queryClient = useQueryClient();
  const [memberships, setMemberships] = useState<MembershipEntry[]>([]);
  const [error, setError] = useState<string | null>(null);

  // 복구 가능한 조직 조회
  const { data: restorableData, isLoading: restorableLoading } = useQuery({
    queryKey: ['admin', 'users', user?.id, 'restorable-organizations'],
    queryFn: () => adminApi.getRestorableOrganizations(user!.id),
    enabled: isOpen && !!user?.id,
  });

  const restorableOrgs: RestorableOrg[] = restorableData?.data?.restorable || [];
  const skippedOrgs: SkippedOrg[] = restorableData?.data?.skipped || [];

  // 조직(구독) 목록 조회 (GROUP 조직만, 추가 멤버십용)
  const { data: orgsData, isLoading: orgsLoading } = useQuery({
    queryKey: ['admin', 'subscriptions'],
    queryFn: () => adminApi.getSubscriptions({ limit: 100 }),
    enabled: isOpen,
  });

  // getSubscriptions는 이미 비즈니스 조직(category != WORKSPACE)만 반환
  const businessOrgs = orgsData?.data || [];
  // 역할 select 후보 — 활성 시스템 역할(`/admin/rbac/roles`)
  const { options: roleOptions } = useSystemRoleOptions(isOpen);

  // 초기화
  useEffect(() => {
    if (isOpen) {
      setMemberships([]);
      setError(null);
    }
  }, [isOpen, user?.id]);

  // 복원 mutation
  const restoreMutation = useMutation({
    mutationFn: () =>
      adminApi.restoreUser(user!.id, {
        memberships: memberships.length > 0 ? memberships : undefined,
      }),
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
        onSuccess();
        onClose();
      } else {
        setError(data.error?.message || '복원에 실패했습니다.');
      }
    },
    onError: (err) => {
      setError(extractApiError(err).message);
    },
  });

  const handleAddMembership = () => {
    // 아직 선택되지 않은 첫 번째 조직 선택.
    // 중복 방지 Set과 비교 대상은 모두 회사 id 축 — 반만 바꾸면 중복 방지가 조용히 깨진다.
    const selectedCompanyIds = new Set(memberships.map((m) => m.companyId));
    const availableOrg = businessOrgs.find((org) => !selectedCompanyIds.has(org.companyId));

    if (availableOrg) {
      setMemberships([
        ...memberships,
        { companyId: availableOrg.companyId, roleKey: DEFAULT_MEMBER_ROLE_KEY },
      ]);
    }
  };

  const handleRemoveMembership = (index: number) => {
    setMemberships(memberships.filter((_, i) => i !== index));
  };

  const handleMembershipChange = (
    index: number,
    field: 'companyId' | 'roleKey',
    value: string,
  ) => {
    const updated = [...memberships];
    if (field === 'companyId') {
      updated[index].companyId = value;
    } else {
      updated[index].roleKey = value;
    }
    setMemberships(updated);
  };

  const handleSubmit = () => {
    setError(null);
    restoreMutation.mutate();
  };

  // 이미 선택된 조직 ID 목록 (회사 id 축)
  const selectedCompanyIds = new Set(memberships.map((m) => m.companyId));

  if (!user) return null;

  return (
    // 처리 중에는 닫는 길(오버레이·Esc·✕) 전부를 막는다 — 응답 전에 닫히면 결과를 볼 자리가 사라진다.
    <Modal
      open={isOpen}
      onClose={onClose}
      title="탈퇴 철회"
      size="md"
      closeOnOverlay={!restoreMutation.isPending}
      closeOnEsc={!restoreMutation.isPending}
      showCloseButton={!restoreMutation.isPending}
      closeLocked={restoreMutation.isPending}
    >
      <div className="space-y-6">
        {/* 안내 메시지 */}
        <div className="p-4 bg-green-50 border border-green-200 rounded-card">
          <div className="flex items-start">
            <svg
              className="w-5 h-5 text-green-600 mt-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">탈퇴 철회</h3>
              <p className="mt-1 text-sm text-green-700">
                <strong>{user.member?.name ?? ''}</strong> 사용자의 탈퇴를 철회하고 계정을 복원합니다.
              </p>
            </div>
          </div>
        </div>

        {/* 함께 복구될 조직 섹션 */}
        {restorableLoading ? (
          <div className="text-sm text-gray-500">복구 정보 로딩 중...</div>
        ) : (restorableOrgs.length > 0 || skippedOrgs.length > 0) && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">
              함께 복구될 조직
            </h4>

            {/* 복구될 조직 */}
            {restorableOrgs.length > 0 && (
              <div className="space-y-2 mb-3">
                {restorableOrgs.map((org) => (
                  <div
                    key={org.id}
                    className="flex items-center justify-between p-3 bg-green-50 border border-green-100 rounded-card"
                  >
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-4 h-4 text-green-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span className="text-sm text-gray-900">{org.name}</span>
                    </div>
                    <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                      {org.category === 'WORKSPACE' ? '개인/팀' : '비즈니스'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* 제외될 조직 (별도 삭제 요청) */}
            {skippedOrgs.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-gray-500">
                  아래 조직은 별도로 삭제가 요청되어 복구되지 않습니다:
                </p>
                {skippedOrgs.map((org) => (
                  <div
                    key={org.id}
                    className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-100 rounded-card"
                  >
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-4 h-4 text-yellow-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                      <span className="text-sm text-gray-700">{org.name}</span>
                    </div>
                    <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">
                      삭제 예약됨
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 조직 추가 섹션 */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-gray-700">
              조직 추가 (선택)
            </label>
            {businessOrgs.length > memberships.length && (
              <TextButton onClick={handleAddMembership} className="text-sm">
                + 조직 추가
              </TextButton>
            )}
          </div>
          <p className="text-xs text-gray-500 mb-3">
            복원 시 사용자를 특정 조직에 추가할 수 있습니다. 선택하지 않으면 개인 조직만
            복원됩니다.
          </p>

          {orgsLoading ? (
            <div className="text-sm text-gray-500">조직 목록 로딩 중...</div>
          ) : businessOrgs.length === 0 ? (
            <div className="text-sm text-gray-500">추가 가능한 비즈니스 조직이 없습니다.</div>
          ) : memberships.length === 0 ? (
            <div className="p-4 border border-dashed border-gray-300 rounded-card text-center">
              <p className="text-sm text-gray-500">
                추가할 조직이 없습니다.
                <br />
                <TextButton onClick={handleAddMembership} className="mt-2">
                  + 조직 추가하기
                </TextButton>
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {memberships.map((membership, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 p-3 bg-gray-50 rounded-card"
                >
                  <select
                    value={membership.companyId}
                    onChange={(e) =>
                      handleMembershipChange(index, 'companyId', e.target.value)
                    }
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-control focus:ring-2 focus:ring-brand-500"
                  >
                    {businessOrgs
                      .filter(
                        (org) =>
                          org.companyId === membership.companyId ||
                          !selectedCompanyIds.has(org.companyId),
                      )
                      .map((org) => (
                        // value는 회사 id — RestoreMembershipDto.companyId가 받는 축.
                        <option key={org.id} value={org.companyId}>
                          {org.name}
                        </option>
                      ))}
                  </select>
                  <select
                    value={membership.roleKey}
                    onChange={(e) =>
                      handleMembershipChange(index, 'roleKey', e.target.value)
                    }
                    className="w-32 px-3 py-2 text-sm border border-gray-300 rounded-control focus:ring-2 focus:ring-brand-500"
                  >
                    {roleOptions.map((role) => (
                      <option key={role.key} value={role.key}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => handleRemoveMembership(index)}
                    className="p-2 text-gray-400 hover:text-red-500"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div className="p-3 bg-red-50 text-red-600 text-sm rounded-card">{error}</div>
        )}

        {/* 액션 버튼 */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="secondary" onClick={onClose} disabled={restoreMutation.isPending}>
            취소
          </Button>
          <Button variant="success" onClick={handleSubmit} disabled={restoreMutation.isPending}>
            {restoreMutation.isPending ? '처리 중...' : '탈퇴 철회'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
